// Frontend RBAC access helpers.
//
// On login the backend (when ENABLE_RBAC is on) returns the screens the user
// may see; we persist them in localStorage as `rbac_screens`. These helpers use
// that list to decide whether the user may open a given /app route.
//
// Fail-open: when `rbac_screens` is absent (RBAC disabled on the backend) every
// route is allowed, so the app behaves exactly as before.

// Master list of every role-gated screen path the RBAC seed knows about.
// Mirrors services/rbacSeed.js SCREENS[].path. Any /app route NOT in this list
// is treated as a utility/detail route and is always allowed.
export const ALL_SCREEN_PATHS = [
  "/app/schooldetails",
  "/app/student-charge-master",
  "/app/users-and-roles",
  "/app/app-version",
  "/app/addclass",
  "/app/subject",
  "/app/lesson",
  "/app/registrationtrainer",
  "/app/trainer",
  "/app/academicyear",
  "/app/iconmaster",
  "/app/superactivity",
  "/app/activitytabel",
  "/app/subactivity",
  "/app/foodmaster",
  "/app/soundmaster",
  "/app/movablemaster",
  "/app/cursivemaster",
  "/app/addbranchschool",
  "/app/studentdetails",
  "/app/teacher",
  "/app/section",
  "/app/specialobservation",
  "/app/agereports",
  "/app/dayreport",
  "/app/today-teacher-report",
  "/app/push-notifications",
  "/app/keywiseactivity",
  "/app/fee",
  "/app/enquiry",
  "/app/foodmenu",
  "/app/handbook",
  "/app/notelist",
  "/app/schedule",
  "/app/promotion",
  "/app/studentactivity",
  "/app/quickactivity",
  "/app/attendence",
  "/app/calenderassignment",
  "/app/activitytrackertable",
  "/app/activityanalysis",
  "/app/teachernotes",
  "/app/teacher-report-ratings",
  "/app/reports",
  "/app/event",
  "/app/teacherlesson",
  "/app/sound",
  "/app/movablealphabets",
  "/app/cursive",
  "/app/activityreport",
  "/app/reportofactivities",
  "/app/imagesstore",
  "/app/trainingsession",
  "/app/assignment",
  "/app/parent-notifications",
  "/app/studentEvents",
  "/app/parentactivitytrackertable",
  "/app/studentstory",
  "/app/milestones-memories",
  "/app/parentfee",
  "/app/parentfoodmenu",
  "/app/parenthandbook",
  "/app/note",
  "/app/helpfullinformation",
  "/app/parent-student-profile",
];

const stripTrailingSlash = (p) =>
  p && p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;

// True only when the backend sent a screen list (i.e. RBAC is enforced).
export const isRbacEnabled = () => {
  try {
    const raw = localStorage.getItem("rbac_screens");
    if (!raw) return false;
    const arr = JSON.parse(raw);
    return Array.isArray(arr);
  } catch {
    return false;
  }
};

// The set of screen paths the current user is allowed to open.
export const getAllowedScreenPaths = () => {
  try {
    const raw = localStorage.getItem("rbac_screens");
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((s) => (typeof s === "string" ? s : s && s.path))
      .filter(Boolean);
  } catch {
    return [];
  }
};

// Decide whether the current pathname may be rendered for this user.
export const isPathAllowed = (pathname) => {
  if (!isRbacEnabled()) return true;

  const path = stripTrailingSlash(String(pathname || ""));

  // Find the guarded screen this path belongs to (exact, or as a base segment
  // for routes with params like /app/foo/:id).
  const guardedBase = ALL_SCREEN_PATHS.find(
    (base) => path === base || path.startsWith(`${base}/`)
  );

  // Not a role-gated screen -> always allow (utility / detail routes).
  if (!guardedBase) return true;

  const allowed = getAllowedScreenPaths().map(stripTrailingSlash);
  return allowed.some(
    (base) => path === base || path.startsWith(`${base}/`)
  );
};
